import type {
  ApiError, Artefact, EngagementPage, ListFilter, StartedEngagement, UploadedDocument,
} from './types'

/** A refusal the caller can render. Carries the correlation id and never a stack. */
export class ApiFailure extends Error {
  readonly status: number
  readonly code: string
  readonly correlationId: string

  constructor(status: number, error: ApiError) {
    super(error.message)
    this.name = 'ApiFailure'
    this.status = status
    this.code = error.code
    this.correlationId = error.correlationId
  }
}

/**
 * The BFF, behind one object. `04 §2`.
 *
 * <p><b>ONE ORIGIN.</b> Every path is relative — the SPA and the API share
 * `advisor.agenticcouncil.app` through the SWA linked backend (R-3), and there is no base URL to
 * configure. A client that took one would be a client that could be pointed somewhere else.</p>
 *
 * <p><b>A FRESH TOKEN PER REQUEST.</b> `token()` is the MSAL silent acquisition, which is a cache
 * hit until it is not; asking each time is what makes an expiry invisible to the caller.</p>
 */
export class EiaClient {
  private readonly token: () => Promise<string>

  constructor(token: () => Promise<string>) {
    this.token = token
  }

  /** The caller's engagements, newest first. Sorted `lastActivityAt DESC, id DESC` by the server. */
  async list(options: { limit?: number; cursor?: string; filter?: ListFilter } = {})
    : Promise<EngagementPage> {
    const query = new URLSearchParams()
    if (options.limit !== undefined) query.set('limit', String(options.limit))

    // THE CURSOR IS OPAQUE AND IS PASSED BACK UNREAD. "A client that can construct a cursor can
    // address another partition."
    if (options.cursor) query.set('cursor', options.cursor)
    if (options.filter) query.set('filter', options.filter)

    const body = await this.json<{ engagements?: unknown; items?: unknown; cursor?: string | null }>(
      `/api/engagements${query.size > 0 ? `?${query}` : ''}`)

    const items = (Array.isArray(body.items) ? body.items
      : Array.isArray(body.engagements) ? body.engagements : []) as EngagementPage['items']

    return { items, nextCursor: body.cursor ?? null, hasMore: Boolean(body.cursor) }
  }

  /**
   * Start one. **202**, and the ask summary comes back synchronously with a deterministic
   * fallback — CREATING AN ENGAGEMENT MUST NEVER FAIL BECAUSE A SUMMARISER WAS BUSY (`AC-ENG-11`).
   */
  start(prompt: string, idempotencyKey: string): Promise<StartedEngagement> {
    return this.json<StartedEngagement>('/api/engagements', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({ prompt }),
    })
  }

  /** One engagement. 404 if not owned — never 403. */
  /**
   * How many engagements the caller has in each filter, for the workspace's tabs.
   *
   * <p>A SEPARATE CALL from the list, because the list is fetched again on every filter change and
   * on every page of results while the counts change only when an engagement does.</p>
   */
  counts(): Promise<Record<string, number>> {
    return this.json<Record<string, number>>('/api/engagements/counts')
  }

  detail(engagementId: string): Promise<unknown> {
    return this.json(`/api/engagements/${encodeURIComponent(engagementId)}`)
  }

  /** The bounded recovery snapshot. NOT A ROUTINE CALL — it is what `replay_incomplete` leads to. */
  state(engagementId: string): Promise<unknown> {
    return this.json(`/api/engagements/${encodeURIComponent(engagementId)}/state`)
  }

  /**
   * Answer the open gate.
   *
   * <p><b>`approved` IS NEVER SENT.</b> The server strips it and re-derives it, and this client does
   * not offer a way to set it — a client that could assert `approved: true` has defeated every gate
   * in the system with one field, and the surest version of that rule is having no such field.</p>
   */
  answer(engagementId: string, gateId: string, decision: string,
         extra: Record<string, unknown> = {}): Promise<unknown> {
    const body: Record<string, unknown> = { ...extra, gateId, decision }
    delete body['approved']

    return this.json(`/api/engagements/${encodeURIComponent(engagementId)}/gate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  }

  /** Cooperative cancel. Idempotent. */
  cancel(engagementId: string): Promise<unknown> {
    return this.json(`/api/engagements/${encodeURIComponent(engagementId)}/cancel`,
                     { method: 'POST' })
  }

  /**
   * A short-lived, brokered URL for one artefact.
   *
   * <p><b>A DOWNLOAD IS A NAVIGATION, AND A NAVIGATION CANNOT CARRY A BEARER.</b> The artefact
   * routes are token-protected, so an `<a href>` to one and an `<iframe src>` pointing at one both
   * arrive without an `Authorization` header and are answered 401 — which is what the artefact card
   * and the report overlay did (B-162). This asks for the URL with the token instead, and what
   * comes back carries its own short-lived credential in its query string.</p>
   */
  async artefactLink(engagementId: string, name: string): Promise<string> {
    const body = await this.json<{ url: string }>(
      `/api/engagements/${encodeURIComponent(engagementId)}`
      + `/artefacts/${encodeURIComponent(name)}?as=link`)
    return body.url
  }

  /**
   * The report itself, as text, over an authenticated same-origin request.
   *
   * <p><b>THE PAGE'S OWN CSP IS WHY THIS EXISTS.</b> `staticwebapp.config.json` sets
   * `frame-src 'self'`, so an iframe pointed at the storage account is refused by the page that
   * opened it — the overlay rendered a 0x0 frame and "Open report" did nothing (B-167). A fetch is
   * governed by `connect-src`, which allows `'self'`, and unlike an iframe it can carry the
   * bearer — so the document comes back here and the overlay renders it with `srcdoc`.</p>
   */
  async reportText(engagementId: string, name: 'report.html' | 'report.md'): Promise<string> {
    return this.send(`/api/engagements/${encodeURIComponent(engagementId)}`
                     + `/artefacts/${name}?as=inline`)
      .then((r) => r.text())
  }

  /** The artefacts, with their hashes. */
  async artefacts(engagementId: string): Promise<Artefact[]> {
    const body = await this.json<{ artefacts?: Artefact[] }>(
      `/api/engagements/${encodeURIComponent(engagementId)}/artefacts`)
    return body.artefacts ?? []
  }

  /**
   * Upload one document.
   *
   * <p><b>A FILESYSTEM PATH IS NEVER ASKED FOR, SHOWN OR ACCEPTED.</b> In Azure there is no
   * filesystem to name, so the rule holds more strongly and for a simpler reason — this takes a
   * `File` from an `<input type="file">` and nothing else.</p>
   */
  upload(engagementId: string, file: File): Promise<UploadedDocument> {
    const form = new FormData()
    form.append('file', file)

    return this.json<UploadedDocument>(
      `/api/engagements/${encodeURIComponent(engagementId)}/documents`,
      { method: 'POST', body: form })
  }

  private async json<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await this.send(path, init)

    if (response.status === 204) return undefined as T
    return await response.json() as T
  }

  /**
   * One authenticated request, with the refusal model applied.
   *
   * <p>Separated from <see cref="json"/> so a body that is NOT JSON — the report, which is
   * markdown or HTML — goes through the same auth and the same error handling rather than growing
   * a second copy of both.</p>
   */
  private async send(path: string, init: RequestInit = {}): Promise<Response> {
    const response = await fetch(path, {
      ...init,
      headers: { ...init.headers, Authorization: `Bearer ${await this.token()}` },
      cache: 'no-store',
    })

    if (!response.ok) {
      // THE ERROR MODEL, OR A STAND-IN FOR IT. A refusal that did not carry the shape is still a
      // refusal, and inventing a correlation id would be worse than admitting there is none.
      let error: ApiError = { code: 'unknown', message: response.statusText, correlationId: '' }
      try {
        const parsed = await response.json() as { error?: ApiError }
        if (parsed.error) error = parsed.error
      } catch {
        // The body was not the error model. `statusText` stands.
      }

      throw new ApiFailure(response.status, error)
    }

    return response
  }
}
